export function toDateInput(value) {
  if (!value) return "";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "";
  return date.toISOString().slice(0, 10);
}

export function toDateTimePayload(value) {
  return value ? `${value}T00:00:00` : null;
}

export function tagIdsForLead(tags = [], options = []) {
  const byName = new Map(options.map((item) => [String(item.name), String(item.id)]));
  return (tags || []).map((item) => {
    if (item && typeof item === "object" && item.id !== undefined) return String(item.id);
    const text = String(item);
    return byName.get(text) || text;
  });
}

export function payloadIds(values = []) {
  return values.map((value) => {
    const number = Number(value);
    return Number.isNaN(number) ? value : number;
  });
}

export function parseChecklist(value = "") {
  return String(value || "")
    .split(/\r?\n/)
    .map((line) => line.replace(/^[-*+]\s*/, "").trim())
    .filter(Boolean)
    .map((line, index) => ({
      id: `task-${index}-${line}`,
      done: /^\[(x|X)\]\s*/.test(line),
      label: line.replace(/^\[(x|X| )\]\s*/, "").trim(),
    }));
}

export function serializeChecklist(items = []) {
  return (items || [])
    .filter((item) => String(item.label || "").trim())
    .map((item) => `[${item.done ? "x" : " "}] ${String(item.label || "").trim()}`)
    .join("\n");
}

export function buildLeadDraft(lead, tagOptions) {
  return {
    stageLabel: lead.stageLabel || "New Lead",
    ownerId: String(lead.ownerId || ""),
    tagIds: tagIdsForLead(lead.tags || [], tagOptions),
    nextFollowUpAt: toDateInput(lead.nextFollowUpAt),
    lastUpdateNote: lead.lastUpdateNote === "-" ? "" : lead.lastUpdateNote,
    important: Boolean(lead.important),
    checklist: parseChecklist(lead.actionItem),
    message: lead.message === "-" ? "" : lead.message,
    // Lead type is optional: an unknown type stays blank, never a made-up "Not Gathered".
    typeLabel: lead.leadType || "",
    estimatedValue: lead.estimatedValue || "",
    upfrontValue: lead.upfrontValue || "",
    dealSize: lead.dealSize || "",
    // The lead's own currency, as stored; never a hard-wired one.
    currency: lead.currency || "",
    // Editable attribution + firmographics (About panel + header icons).
    product: lead.product && lead.product !== "-" ? lead.product : "",
    channel: lead.channel && lead.channel !== "-" ? lead.channel : "",
    industry: lead.industry && lead.industry !== "-" ? lead.industry : "",
    companyName: lead.companyName && lead.companyName !== "-" ? lead.companyName : "",
  };
}

// The stage a pick-list really shows: the one chosen if it is on the list, else the first. The
// select box and the value that gets sent can therefore never disagree.
export function shownStage(chosen, stages) {
  return stages.includes(chosen) ? chosen : stages[0];
}

// A person's name as typed in Add Lead: letters of any language (accents and combining marks
// included), spaces, dots, hyphens and apostrophes.
export function isValidContactName(value) {
  return /^[\p{L}\p{M}\s.\-']+$/u.test(value);
}

// What a money box may hold: digits and one dot. "1..5" or "1.2.3" would otherwise reach Number()
// as NaN, which JSON turns into null and the save then clears or rejects the value.
export function moneyInput(text) {
  return String(text ?? "").replace(/[^0-9.]/g, "").replace(/(\..*)\./g, "$1");
}

function moneyAmount(text) {
  const amount = Number(text);
  return text && Number.isFinite(amount) ? Math.max(0, amount) : 0;
}

export function buildLeadPayload(lead, draft) {
  return {
    owner: draft.ownerId || null,
    stage: draft.stageLabel || lead.stageLabel || "New Lead",
    tags: payloadIds(draft.tagIds),
    next_follow_up_at: toDateTimePayload(draft.nextFollowUpAt),
    latest_comment: draft.lastUpdateNote || "",
    action_item: serializeChecklist(draft.checklist),
    estimated_value: moneyAmount(draft.estimatedValue),
    upfront_value: moneyAmount(draft.upfrontValue),
    deal_size: moneyAmount(draft.dealSize),
    currency: draft.currency || "",
    is_important: draft.important,
    lead_type: draft.typeLabel || "",
    message: draft.message || "",
    // Attribution axes + firmographics — editable from the lead detail page.
    // `attr_source` is the RevOps channel; the backend auto-derives the campaign.
    product: draft.product || "",
    attr_source: draft.channel || "",
    industry: draft.industry || "",
    company_name: draft.companyName || "",
    // No `metadata`: this form never edits it, so a save must never send it (it used to send {}
    // when the page was opened from the lead detail reply, wiping what an import or the recorder stored).
  };
}

// Only what the person changed since this form was last in step with the server. Sending the whole
// form would put back, for every untouched field, the value it had when the page was opened, and so
// undo what a colleague changed in the meantime (owner, stage, tags...). Nothing changed: empty.
export function changedLeadPayload(lead, baseDraft, nextDraft) {
  const before = buildLeadPayload(lead, baseDraft);
  const after = buildLeadPayload(lead, nextDraft);
  const changed = {};
  for (const key of Object.keys(after)) {
    if (JSON.stringify(after[key]) !== JSON.stringify(before[key])) changed[key] = after[key];
  }
  return changed;
}

export function leadCaptureMoneyFields(formData) {
  const fields = {};
  const estimated = Number(formData.estimated_value);
  if (formData.estimated_value && Number.isFinite(estimated)) fields.estimated_value = Math.max(0, estimated);
  const upfront = Number(formData.upfront_value);
  if (formData.upfront_value && Number.isFinite(upfront)) fields.upfront_value = Math.max(0, upfront);
  const dealSize = Number(formData.deal_size);
  if (formData.deal_size && Number.isFinite(dealSize)) fields.deal_size = Math.max(0, dealSize);
  return fields;
}
